/**
 * STATISTICS' "ARTICLES" TAB, REDRAWN (design 18, section 4; the owner, 2026-10-05): how each
 * article did over a period, most views first (ruling 5), with its cover (ruling 6) and, on a
 * press, its own figures day by day, each with the Overview's (i) (design 24, batch 2).
 *
 *   - THE PERIOD: the period button's set periods and custom range (design 24, T15), and last the
 *     server's usual period ("Last 365 days" under today's policy), chosen first and read with NO
 *     range, as before - so the table never waits on another read. Its days are the no-range
 *     `getDashboard`'s when the screen has them (the same `resolveDayRange` answers both reads,
 *     analytics-window.ts), else learnt from this read's own first answer; the button stands once
 *     either is known. "All articles" opens this tab on the Overview's period and figure.
 *   - A TABLE WHERE IT FITS (720px and up): the article - cover, title (the way to it), when it was
 *     published - then Views with a thin bar against the largest on screen, Countable views, All
 *     time, Likes, Comments, Saves, and Shares / Read-through once any row has them measured. Every
 *     header sorts (`aria-sort` on the sorted one). Narrower, ONE CARD PER ARTICLE and a "Sort by"
 *     menu - no table that scrolls sideways on a phone.
 *   - EVERY ROW IS READ (./reads.ts, `useAllArticleStats`) and sorted here; 25 drawn, then 25 more.
 *     Sorting orders the rows: no column is added up (a per-article sum is not the Overview's
 *     figure - ../ArticleStatsRoute.tsx, point 3).
 *   - ★ NO READERS COLUMN (ruling 2). Readers are counted per day, and a reader who came back on
 *     three days would be three readers in a period's row - so the row has no such figure, and one
 *     note under the table says so; the Overview has the period's readers worked out properly.
 *   - ARTICLES NEVER PUBLISHED ARE LEFT OUT: a draft has no figures to compare.
 *   - BOTH VIEW FIGURES STAY LABELLED, and Countable views says it is one per reader a day;
 *     the first article that earns nothing by design keeps its words (D7), unless it came over
 *     from before (design 14, F2).
 */
import { Anchor, NativeSelect, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ChartColumn, Image as ImageIcon, PeriodPicker, X } from '@moonx/ui';
import type { ArticleStats } from '../../../features/cabinet/api';
import { allTimeViewsOf } from '../../../features/cabinet/all-time-views';
import {
  EmptyState,
  ErrorState,
  FigureBadge,
  FigureSwitch,
  RefreshingNote,
} from '../../../features/cabinet/components';
import type { SwitchFigure } from '../../../features/cabinet/components';
import {
  formatCount,
  formatDay,
  formatDayRange,
  formatPercent,
} from '../../../features/cabinet/format';
import { daysIn, isDay, periodButtonFor, rangeOf } from '../../../features/cabinet/ranges';
import type { DayRange, PeriodButtonProps, PeriodChoice } from '../../../features/cabinet/ranges';
import { emulatorImageUrl, servedUrlOnError } from '../../../features/storage/emulator-image-url';
import { InAppAnchor } from '../../../features/shell/InAppAnchor';
import { Trans, useTranslation } from '../../../i18n';
import { editorHrefFor } from '../EditorRoute';
import { FigureInfo } from '../StatsOverview';
import type { ArticlesT } from '../articles-i18n';
import { DayChart } from './DayChart';
import {
  ARTICLE_FIGURES,
  counted,
  dayValue,
  figureBadge,
  figureLabel,
  figureNoneOnDays,
  figureOf,
  periodWords,
  rowTile,
} from './figures';
import type { ArticleFigureKey } from './figures';
import { answers, daysKey, useAllArticleStats, useArticleCovers, useDays } from './reads';
import type { StatsKind } from './reads';
import { videoEditorHrefFor } from '../../videos/paths';
import classes from './articles-panel.module.css';

/** A set period, a custom range, or the server's usual period. */
export type ArticlesPeriod = PeriodChoice | { readonly kind: 'usual' };

/** What the table can be sorted by. */
export type SortKey = ArticleFigureKey | 'allTime' | 'shares' | 'readThrough' | 'published';

export interface ArticlesPanelProps {
  /** The server's usual period - the no-range `getDashboard`'s days, when the screen has them. */
  readonly usual: DayRange | undefined;
  /** Where "Go to your articles" goes when there is nothing to show. */
  readonly articlesHref: string;
  /** The period and the order to open on - "All articles" from the Overview. */
  readonly initial?: { readonly period: ArticlesPeriod; readonly sortBy: SortKey } | undefined;
  /** Where the period button goes - the tabs' row; `null` while the tab is not shown. */
  readonly pickerSlot?: HTMLElement | null | undefined;
  /** The same table for the videos, which earn as articles do. */
  readonly kind?: StatsKind | undefined;
}

/** How many rows are drawn at first, and added by "Show more". */
const ROWS_AT_ONCE = 25;

/** From this width of the panel the table fits; below it, cards. */
const TABLE_WIDTH = 720;

export function ArticlesPanel({
  usual,
  articlesHref,
  initial,
  pickerSlot,
  kind = 'article',
}: ArticlesPanelProps) {
  const { t } = useTranslation('articles');
  const video = kind === 'video';
  const [period, setPeriod] = useState<ArticlesPeriod>(initial?.period ?? { kind: 'usual' });
  const [sortBy, setSortBy] = useState<SortKey>(initial?.sortBy ?? 'views');
  const [shown, setShown] = useState(ROWS_AT_ONCE);
  const [opened, setOpened] = useState<ArticleStats | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const { ref, width } = useElementSize();
  const asCards = width > 0 && width < TABLE_WIDTH;
  // The usual period's days, learnt from the no-range answer when the screen could not hand them in.
  const [learnt, setLearnt] = useState<DayRange | undefined>(undefined);
  const known = usual ?? learnt;

  const range = useMemo(
    () => (period.kind === 'usual' || known === undefined ? known : rangeOf(period, known.to)),
    [period, known],
  );
  // The usual period is read with NO range, as before: the answer's days are the server's own.
  const asked = period.kind === 'usual' ? undefined : range;
  const stats = useAllArticleStats(asked, kind);
  const covers = useArticleCovers(kind);
  const here = answers(stats.data, asked) ? stats.data : undefined;
  // Adjusting state during render: React's shape for "derive once from a value that arrives later".
  if (known === undefined && here !== undefined && isDay(here.from) && isDay(here.to)) {
    setLearnt({ from: here.from, to: here.to });
  }

  const rows = useMemo(
    () =>
      sortRows(
        (here?.items ?? []).filter((row) => row.publishedAt !== null),
        sortBy,
      ),
    [here, sortBy],
  );
  const measured = {
    shares: rows.some((row) => row.shares.state !== 'not_reported_yet'),
    readThrough: rows.some((row) => row.readThrough.state !== 'not_reported_yet'),
  };
  const largestViews = Math.max(1, ...rows.map((row) => figureOf(row.views) ?? 0));
  const words = range === undefined ? undefined : wordsOf(period, range, t);
  const coverOf = (articleId: string): string | undefined => covers.data?.get(articleId);

  const open = (row: ArticleStats, from: HTMLElement) => {
    opener.current = from;
    setOpened(row);
  };
  const close = () => {
    setOpened(null);
    opener.current?.focus();
  };

  return (
    <Stack gap="lg" component="section" aria-labelledby="per-article-heading" ref={ref}>
      <Stack gap={4}>
        <Title order={2} size="h3" id="per-article-heading">
          {t(video ? 'stats.videos.title' : 'stats.articles.title')}
        </Title>
        <Text size="sm" c="dimmed">
          {t(video ? 'stats.videos.intro' : 'stats.articles.intro')}
        </Text>
      </Stack>

      {known === undefined
        ? null
        : inSlot(
            pickerSlot,
            <PeriodPicker
              label={t('stats.period.picker')}
              {...articlesButton(
                period,
                known,
                (next) => {
                  setPeriod(next);
                  setShown(ROWS_AT_ONCE);
                  setOpened(null);
                },
                t,
              )}
            />,
          )}

      {here === undefined ? (
        stats.status === 'error' ? (
          <ErrorState
            title={t('common.figuresDidNotLoad')}
            error={stats.error}
            onRetry={stats.refetch}
          />
        ) : (
          <Stack
            gap="sm"
            role="status"
            aria-busy="true"
            aria-label={t(video ? 'stats.videos.reading' : 'stats.articles.reading')}
          >
            <Skeleton height={56} radius="md" />
            <Skeleton height={56} radius="md" />
            <Skeleton height={56} radius="md" />
          </Stack>
        )
      ) : rows.length === 0 ? (
        <EmptyState
          headline={t(video ? 'stats.videos.emptyTitle' : 'stats.articles.emptyTitle')}
          explanation={t('stats.articles.emptyText')}
          action={
            <Anchor href={articlesHref} size="sm">
              {t(video ? 'stats.videos.goToArticles' : 'stats.articles.goToArticles')}
            </Anchor>
          }
        />
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {here.from === undefined || here.to === undefined
              ? t('stats.articles.usualPeriod')
              : t('stats.articles.bothDays', { range: formatDayRange(here.from, here.to) })}{' '}
            {t(video ? 'stats.videos.count' : 'stats.articles.count', {
              count: rows.length,
              value: formatCount(rows.length),
            })}
            {here.complete ? '' : t('stats.articles.firstOnly')}
          </Text>
          {stats.status === 'loading' ? <RefreshingNote label={t('common.refreshing')} /> : null}

          {opened === null || range === undefined || words === undefined ? null : (
            <DayByDay row={opened} range={range} words={words} onClose={close} />
          )}

          {asCards ? (
            <>
              <NativeSelect
                label={t('stats.articles.sortBy')}
                value={sortBy}
                data={sortChoices(measured, t)}
                onChange={(event) => {
                  setSortBy(event.currentTarget.value as SortKey);
                }}
                w={220}
              />
              <ul
                className={classes.cards}
                aria-label={t(video ? 'stats.videos.figuresLabel' : 'stats.articles.figuresLabel')}
              >
                {rows.slice(0, shown).map((row) => (
                  <li key={row.articleId} className={classes.card}>
                    <ArticleHead
                      row={row}
                      cover={coverOf(row.articleId)}
                      onOpen={open}
                      video={video}
                    />
                    <dl className={classes.cardFigures}>
                      {cardFigures(row, t).map(([label, value, badge]) => (
                        <div key={label}>
                          <dt>
                            {label}
                            {badge === undefined ? null : (
                              <>
                                {' '}
                                <FigureBadge>{badge}</FigureBadge>
                              </>
                            )}
                          </dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Table.ScrollContainer
              minWidth={TABLE_WIDTH}
              type="native"
              className={classes.tableCard}
              tabIndex={0}
              role="region"
              aria-label={t(video ? 'stats.videos.figuresLabel' : 'stats.articles.figuresLabel')}
            >
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Caption className={classes.caption}>
                  {t(video ? 'stats.videos.caption' : 'stats.articles.caption')}
                </Table.Caption>
                <Table.Thead>
                  <Table.Tr>
                    <SortHeader
                      label={t(
                        video ? 'stats.videos.columns.article' : 'stats.articles.columns.article',
                      )}
                      sortKey="published"
                      sortBy={sortBy}
                      onSort={setSortBy}
                      left
                    />
                    {columns(measured, t).map(([key, label]) => (
                      <SortHeader
                        key={key}
                        label={label}
                        badge={key === 'countedViews' ? figureBadge(key, t) : undefined}
                        sortKey={key}
                        sortBy={sortBy}
                        onSort={setSortBy}
                      />
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.slice(0, shown).map((row) => (
                    <Table.Tr key={row.articleId}>
                      <Table.Th scope="row" className={classes.articleCell}>
                        <ArticleHead
                          row={row}
                          cover={coverOf(row.articleId)}
                          onOpen={open}
                          video={video}
                        />
                      </Table.Th>
                      <Table.Td className={classes.number}>
                        <Figure value={figureOf(row.views)} />
                        <span aria-hidden="true" className={classes.viewsTrack}>
                          <span
                            className={classes.viewsBar}
                            style={{
                              width: `${String(((figureOf(row.views) ?? 0) / largestViews) * 100)}%`,
                            }}
                          />
                        </span>
                      </Table.Td>
                      <Table.Td className={classes.number}>
                        <Figure value={figureOf(row.countedViews)} />
                      </Table.Td>
                      <Table.Td className={classes.number}>
                        <AllTime row={row} />
                      </Table.Td>
                      <Table.Td className={classes.number}>
                        <Figure value={figureOf(row.likes)} />
                      </Table.Td>
                      <Table.Td className={classes.number}>
                        <Figure value={figureOf(row.comments)} />
                      </Table.Td>
                      <Table.Td className={classes.number}>
                        <Figure value={figureOf(row.saves)} />
                      </Table.Td>
                      {measured.shares ? (
                        <Table.Td className={classes.number}>
                          <Figure value={figureOf(row.shares)} />
                        </Table.Td>
                      ) : null}
                      {measured.readThrough ? (
                        <Table.Td className={classes.number}>{readThroughWords(row, t)}</Table.Td>
                      ) : null}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          {rows.length > shown ? (
            <Anchor
              component="button"
              type="button"
              size="sm"
              fw={700}
              className={classes.more}
              onClick={() => {
                setShown((count) => count + ROWS_AT_ONCE);
              }}
            >
              {t('stats.articles.showMore', {
                value: formatCount(Math.min(ROWS_AT_ONCE, rows.length - shown)),
              })}
            </Anchor>
          ) : null}

          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              <Trans
                t={t}
                i18nKey={video ? 'stats.videos.viewsNote' : 'stats.articles.viewsNote'}
                components={{ strong: <strong /> }}
              />
            </Text>
            <Text size="xs" c="dimmed">
              {t(video ? 'stats.videos.readersNote' : 'stats.articles.readersNote')}
            </Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

/* ---------------------------------------------------------------------------- the choices */

/** The period button in the tabs' row, nowhere while this tab is hidden, here with no row. */
function inSlot(slot: HTMLElement | null | undefined, picker: ReactNode): ReactNode {
  return slot === undefined ? picker : slot === null ? null : createPortal(picker, slot);
}

interface Measured {
  readonly shares: boolean;
  readonly readThrough: boolean;
}

/** The figure columns, in order: Shares and Read-through only once any row has them measured. */
function columnKeys(measured: Measured): readonly Exclude<SortKey, 'published'>[] {
  return [
    'views',
    'countedViews',
    'allTime',
    'likes',
    'comments',
    'saves',
    ...(measured.shares ? (['shares'] as const) : []),
    ...(measured.readThrough ? (['readThrough'] as const) : []),
  ];
}

function columns(measured: Measured, t: ArticlesT): readonly (readonly [SortKey, string])[] {
  return columnKeys(measured).map((key) => [key, t(`stats.articles.columns.${key}`)] as const);
}

function sortChoices(measured: Measured, t: ArticlesT): { value: SortKey; label: string }[] {
  return [
    ...columnKeys(measured).map((value) => ({
      value,
      label: t(`stats.articles.sortMost.${value}`),
    })),
    { value: 'published' as const, label: t('stats.articles.sortMost.published') },
  ];
}

/* ------------------------------------------------------------------------------ the order */

function sortValue(row: ArticleStats, key: SortKey): number {
  switch (key) {
    case 'allTime':
      return allTimeViewsOf(row) ?? -1;
    case 'readThrough':
      return row.readThrough.state === 'ok' ? (row.readThrough.percent ?? -1) : -1;
    case 'published':
      return row.publishedAt === null ? -1 : Date.parse(row.publishedAt);
    case 'shares':
      return figureOf(row.shares) ?? -1;
    default:
      return figureOf(rowTile(row, key)) ?? -1;
  }
}

/** Largest first; equal figures newest first. Only an ORDER - nothing is added up. */
export function sortRows(rows: readonly ArticleStats[], key: SortKey): ArticleStats[] {
  return [...rows].sort(
    (a, b) =>
      sortValue(b, key) - sortValue(a, key) ||
      (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''),
  );
}

/* ------------------------------------------------------------------------------- the parts */

function SortHeader({
  label,
  badge,
  sortKey,
  sortBy,
  onSort,
  left = false,
}: {
  readonly label: string;
  readonly badge?: string | undefined;
  readonly sortKey: SortKey;
  readonly sortBy: SortKey;
  readonly onSort: (key: SortKey) => void;
  readonly left?: boolean;
}) {
  const { t } = useTranslation('articles');
  const sorted = sortKey === sortBy;
  return (
    <Table.Th
      scope="col"
      {...(left ? {} : { className: classes.number })}
      {...(sorted ? { 'aria-sort': 'descending' as const } : {})}
    >
      {/* Largest first, always; the article column sorts newest first. The sorted column's arrow
          is drawing - `aria-sort` on the header says the order. */}
      <button
        type="button"
        className={classes.sort}
        data-sorted={sorted ? 'true' : undefined}
        onClick={() => {
          onSort(sortKey);
        }}
      >
        {label}
        {badge === undefined ? null : (
          <>
            {' '}
            <FigureBadge>{badge}</FigureBadge>
          </>
        )}
        {left ? (
          <span className={classes.visuallyHidden}> {t('stats.articles.sortNewestHint')}</span>
        ) : null}
        {sorted ? <ArrowDown size={12} aria-hidden /> : null}
      </button>
    </Table.Th>
  );
}

function ArticleHead({
  row,
  cover,
  onOpen,
  video,
}: {
  readonly row: ArticleStats;
  readonly cover: string | undefined;
  readonly onOpen: (row: ArticleStats, from: HTMLElement) => void;
  readonly video: boolean;
}) {
  const { t } = useTranslation('articles');
  const title = row.title.trim() === '' ? t('common.untitled') : row.title;
  return (
    <div className={classes.head}>
      {cover === undefined ? (
        <span className={classes.thumb} aria-hidden="true">
          <ImageIcon size={16} />
        </span>
      ) : (
        <img
          className={classes.thumb}
          src={emulatorImageUrl(cover)}
          onError={servedUrlOnError(cover)}
          alt=""
          loading="lazy"
        />
      )}
      <span className={classes.headWords}>
        <InAppAnchor
          href={video ? videoEditorHrefFor(row.articleId) : editorHrefFor(row.articleId)}
          className={classes.title}
          c="inherit"
          underline="always"
        >
          {title}
        </InAppAnchor>
        <span className={classes.meta}>
          {row.publishedAt === null
            ? t('stats.articles.neverPublished')
            : t('stats.articles.published', { day: formatDay(row.publishedAt.slice(0, 10)) })}
          {row.publicationOrdinal === 1 && row.importedFromOldApp !== true
            ? ` · ${t(video ? 'stats.videos.firstVideo' : 'stats.articles.firstArticle')}`
            : ''}
          {' · '}
          {/* Named with the article, so a list of them is not five "Day by day"s - and the name
              starts with the words on the button (WCAG 2.5.3). An explicit name, because a hidden
              span's text would be read with a space before its colon. */}
          <button
            type="button"
            className={classes.daysButton}
            aria-label={t('stats.articles.dayByDayOf', { title })}
            onClick={(event) => {
              onOpen(row, event.currentTarget);
            }}
          >
            <ChartColumn size={12} aria-hidden />
            {t('stats.articles.dayByDay')}
          </button>
        </span>
      </span>
    </div>
  );
}

function Figure({ value }: { readonly value: number | undefined }) {
  const { t } = useTranslation('articles');
  return value === undefined ? (
    <span className={classes.quiet}>{t('common.notMeasuredYet')}</span>
  ) : (
    <>{formatCount(value)}</>
  );
}

/**
 * Every view the article ever had - or, from an answer with neither `allTimeViews` nor the older
 * `oldAppViews`, the word the table uses for a figure that did not come, never a number in its
 * place (../ArticleStatsRoute.tsx, point 4b).
 */
function AllTime({ row }: { readonly row: ArticleStats }) {
  const { t } = useTranslation('articles');
  const views = allTimeViewsOf(row);
  return views === undefined ? (
    <span className={classes.quiet}>{t('common.notMeasuredYet')}</span>
  ) : (
    <>{formatCount(views)}</>
  );
}

function readThroughWords(row: ArticleStats, t: ArticlesT): ReactNode {
  const tile = row.readThrough;
  if (tile.state !== 'ok' || tile.percent === undefined || tile.percent === null) {
    return <span className={classes.quiet}>{t('common.notMeasuredYet')}</span>;
  }
  return formatPercent(tile.percent > 1 ? tile.percent / 100 : tile.percent);
}

/** Each figure of a card: its name, its value, and its badge where it has one ("Beta"). */
function cardFigures(
  row: ArticleStats,
  t: ArticlesT,
): readonly (readonly [string, ReactNode, (string | undefined)?])[] {
  return [
    [t('stats.articles.columns.views'), <Figure key="v" value={figureOf(row.views)} />],
    [
      t('stats.articles.columns.countedViews'),
      <Figure key="c" value={figureOf(row.countedViews)} />,
      figureBadge('countedViews', t),
    ],
    [t('stats.articles.columns.allTime'), <AllTime key="a" row={row} />],
    [t('stats.articles.columns.likes'), <Figure key="l" value={figureOf(row.likes)} />],
    [t('stats.articles.columns.comments'), <Figure key="m" value={figureOf(row.comments)} />],
    [t('stats.articles.columns.saves'), <Figure key="s" value={figureOf(row.saves)} />],
  ];
}

/* ---------------------------------------------------------------------------- day by day */

function DayByDay({
  row,
  range,
  words,
  onClose,
}: {
  readonly row: ArticleStats;
  readonly range: DayRange;
  readonly words: ReturnType<typeof periodWords>;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('articles');
  const [figure, setFigure] = useState<ArticleFigureKey>('views');
  const days = useDays(range, row.articleId);
  const answer =
    days.data !== undefined && days.data.key === daysKey(range, row.articleId)
      ? days.data
      : undefined;
  const title = row.title.trim() === '' ? t('common.untitled') : row.title;
  const focusOnOpen = (heading: HTMLHeadingElement | null) => {
    heading?.focus();
  };
  const switchFigures: SwitchFigure<ArticleFigureKey>[] = ARTICLE_FIGURES.map((key) => {
    const value = figureOf(rowTile(row, key));
    return {
      key,
      label: figureLabel(key, t),
      badge: figureBadge(key, t),
      // What it means: the Overview's own sentence for the figure of this name, in its own (i).
      info: <FigureInfo name={figureLabel(key, t)}>{t(`stats.info.${key}`)}</FigureInfo>,
      value: value === undefined ? t('common.notMeasuredYet') : formatCount(value),
      quiet: value === undefined,
    };
  });

  return (
    <section className={classes.panel} aria-labelledby={`days-${row.articleId}`}>
      <div className={classes.panelHead}>
        <h3
          className={classes.panelTitle}
          id={`days-${row.articleId}`}
          tabIndex={-1}
          ref={focusOnOpen}
        >
          {t('stats.articles.dayByDayOf', { title })}
        </h3>
        <button
          type="button"
          className={classes.close}
          onClick={onClose}
          aria-label={t('stats.articles.closeDays')}
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <FigureSwitch
        label={t('common.showOnChart')}
        figures={switchFigures}
        pressed={figure}
        onPress={setFigure}
      />
      <div className={classes.panelChart}>
        {answer === undefined ? (
          days.status === 'error' ? (
            <ErrorState
              title={t('common.daysDidNotLoad')}
              error={days.error}
              onRetry={days.refetch}
            />
          ) : (
            <Skeleton height={200} radius="md" aria-hidden="true" />
          )
        ) : answer.days === null ? (
          <Text size="sm" c="dimmed">
            {t('stats.articles.daysNotServed')}
          </Text>
        ) : (
          <DayChart
            title={t('stats.articles.chartTitle', { figure: figureLabel(figure, t), title })}
            worded={(value) => counted(value, figure, t)}
            days={answer.days.days.map((day) => ({ day: day.day, value: dayValue(day, figure) }))}
            published={
              row.publishedAt === null ? undefined : new Set([row.publishedAt.slice(0, 10)])
            }
            these={words.these}
            beforeWords={words.before}
            beforeInSentence={words.beforeInSentence}
            empty={figureNoneOnDays(figure, t)}
          />
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------- the period button */

/**
 * The period button for this tab (design 24, T15): the set periods and a custom range, as on the
 * Overview, then the server's usual period - "Last 365 days" - last, which the tab opens on.
 */
function articlesButton(
  period: ArticlesPeriod,
  usual: DayRange,
  choose: (next: ArticlesPeriod) => void,
  t: ArticlesT,
): PeriodButtonProps<Exclude<ArticlesPeriod['kind'], 'custom'>> {
  // On the usual period the set's own choice is unused: its value and days are the usual's, below.
  const set = periodButtonFor(
    period.kind === 'usual' ? { kind: 'last30' } : period,
    usual.to,
    choose,
  );
  const label = t('stats.articles.lastDays', {
    count: daysIn(usual),
    value: formatCount(daysIn(usual)),
  });
  return {
    ...set,
    presets: [...set.presets, { value: 'usual', label, range: usual }],
    value: period.kind,
    range: period.kind === 'usual' ? usual : set.range,
    onChange: (value, days) => {
      if (value === 'usual') {
        choose({ kind: 'usual' });
      } else {
        set.onChange(value, days);
      }
    },
  };
}

/** The period's words; a custom range is worded by its days, as the usual period is. */
function wordsOf(
  period: ArticlesPeriod,
  range: DayRange,
  t: ArticlesT,
): ReturnType<typeof periodWords> {
  return periodWords(period.kind === 'custom' ? 'usual' : period.kind, range, t);
}
